import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as Icons from 'lucide-react';
import { Reveal, CountUp, RotatingWords, MeshBlobs, useParallax, Carousel, Collapse } from '@/components/renderer/Motion';
import { themeAttrs, themeColors, themeValue, resolveMotion, splitWords, readableColor } from '@/lib/siteTheme';
import '@/components/renderer/site-theme.css';
import { Menu, X, Mail, Phone, MapPin, Check, Quote, Send, Loader2 } from 'lucide-react';
import { pick } from '@/lib/i18n';
import { api } from '@/lib/api';
import { StoreProvider } from '@/lib/storeApi';
import { STORE_BLOCKS, StoreNavControls, StoreTrustStrip } from '@/components/renderer/StoreBlocks';
import { STORE_SECTION_TYPES } from '@/lib/sectionDefs';
import { EXTRA_BLOCKS } from '@/components/renderer/ExtraBlocks';
import { AnnouncementBar } from '@/components/renderer/SiteExtras';

const Icon = ({ name, ...props }) => {
  const C = (typeof name === 'string' && Icons[name]) || Icons.Star;
  return <C {...props} />;
};
/** Always render a string: AI output may put bilingual objects where a plain value is expected. */
const txt = (v, lang) => (v == null ? '' : typeof v === 'object' ? pick(v, lang) : String(v));
const arr = (v) => (Array.isArray(v) ? v : []);

class BlockBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { console.error('Section render failed', this.props.type, err); }
  render() {
    if (this.state.err) return <div className="mx-auto my-6 max-w-3xl rounded-2xl border border-dashed border-rose-300 bg-rose-50 p-6 text-center text-sm text-rose-700" data-testid="section-render-error">Section "{this.props.type}" gagal ditampilkan — periksa isi section di panel kanan. / This section could not be rendered.</div>;
    return this.props.children;
  }
}

const hexToRgb = (hex) => {
  const h = (hex || '#1B2A6B').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

const Container = ({ children, className = '' }) => <div className={`mx-auto max-w-6xl px-5 sm:px-8 ${className}`}>{children}</div>;

const SectionTitle = ({ title, subtitle, lang, center = true }) => (
  <div className={`mb-10 ${center ? 'text-center mx-auto max-w-2xl' : ''}`}>
    {pick(title, lang) && <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: 'var(--site-ink)' }}>{pick(title, lang)}</h2>}
    {pick(subtitle, lang) && <p className="mt-3 text-base sm:text-lg" style={{ color: 'var(--site-muted)' }}>{pick(subtitle, lang)}</p>}
  </div>
);

const Btn = ({ href, children, variant = 'primary', onNavigate }) => {
  const cls = `site-btn site-btn-${variant} inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-colors shadow-sm`;
  return (
    <a href={href || '#'} onClick={(e) => onNavigate?.(e, href)} className={cls}>{children}</a>
  );
};

// ---------- Blocks ----------
const Hero = ({ c, lang, onNavigate, motion }) => {
  const variant = c.variant || (c.align === 'center' || !c.image_url ? 'centered' : 'split');
  const animated = motion !== 'none';
  const imgRef = useRef(null);
  useParallax(imgRef, animated && !!c.parallax && !!c.image_url, variant === 'overlay' ? 0.12 : 0.08);
  const words = splitWords(pick(c.rotating_words, lang));
  const center = variant !== 'split';
  const overlay = variant === 'overlay';
  const ink = overlay ? '#fff' : 'var(--site-ink)';
  const muted = overlay ? 'rgba(255,255,255,0.82)' : 'var(--site-muted)';
  const eyebrow = pick(c.eyebrow, lang);
  const sec = pick(c.secondary_cta_label, lang);
  const Text = (
    <div className={`site-stagger ${center ? 'mx-auto max-w-3xl' : ''}`}>
      {eyebrow && <span className="mb-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide" style={{ background: overlay ? 'rgba(255,255,255,0.16)' : 'rgba(var(--site-primary-rgb),0.1)', color: overlay ? '#fff' : 'var(--site-primary)' }} data-testid="hero-eyebrow">{eyebrow}</span>}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight" style={{ color: ink }}>
        {pick(c.headline, lang)}{words.length > 0 && <> <RotatingWords words={words} animated={animated} style={{ color: overlay ? 'var(--site-secondary)' : 'var(--site-primary)' }} /></>}
      </h1>
      {pick(c.subheadline, lang) && <p className="mt-6 text-lg sm:text-xl leading-relaxed" style={{ color: muted }}>{pick(c.subheadline, lang)}</p>}
      {(pick(c.cta_label, lang) || sec) && (
        <div className={`mt-8 flex flex-wrap gap-3 ${center ? 'justify-center' : ''}`}>
          {pick(c.cta_label, lang) && <Btn href={c.cta_link} onNavigate={onNavigate}>{pick(c.cta_label, lang)}</Btn>}
          {sec && <a href={c.secondary_cta_link || '#'} onClick={(e) => onNavigate?.(e, c.secondary_cta_link)} className="site-btn inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold" style={{ background: overlay ? 'rgba(255,255,255,0.14)' : 'rgba(var(--site-primary-rgb),0.08)', color: overlay ? '#fff' : 'var(--site-primary)' }} data-testid="hero-secondary-cta">{sec}</a>}
        </div>
      )}
    </div>
  );
  if (overlay) {
    return (
      <section className="site-hero-overlay relative flex min-h-[70vh] items-center overflow-hidden py-24 sm:py-32" style={{ background: 'var(--site-primary)' }} data-testid="hero-overlay">
        {c.image_url && <div ref={imgRef} className="absolute -inset-[10%]"><img src={c.image_url} alt="" className="h-full w-full object-cover" fetchpriority="high" decoding="async" /></div>}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,14,30,0.35), rgba(10,14,30,0.72))' }} />
        <Container className="relative w-full text-center">{Text}</Container>
      </section>
    );
  }
  if (variant === 'mesh') {
    return (
      <section className="relative overflow-hidden py-20 sm:py-28" data-testid="hero-mesh">
        <MeshBlobs animated={animated} />
        <Container className="relative text-center">
          {Text}
          {c.image_url && <div ref={imgRef} className="relative mx-auto mt-14 max-w-4xl"><img src={c.image_url} alt="" className="w-full rounded-3xl object-cover aspect-[16/8] shadow-xl" fetchpriority="high" decoding="async" /></div>}
        </Container>
      </section>
    );
  }
  return (
    <section className="relative overflow-hidden py-16 sm:py-24" style={{ background: 'linear-gradient(180deg, rgba(var(--site-primary-rgb),0.06), rgba(255,255,255,0) 70%)' }}>
      <Container>
        <div className={`grid gap-12 items-center ${center ? 'text-center' : 'lg:grid-cols-2'}`}>
          {Text}
          {c.image_url && !center && (
            <div ref={imgRef} className="relative">
              <div className="absolute -inset-4 rounded-[32px]" style={{ background: 'rgba(var(--site-secondary-rgb),0.15)' }} />
              <img src={c.image_url} alt="" className="relative w-full rounded-3xl object-cover aspect-[4/3] shadow-xl" fetchpriority="high" decoding="async" />
            </div>
          )}
          {c.image_url && center && <div ref={imgRef}><img src={c.image_url} alt="" className="mx-auto w-full max-w-4xl rounded-3xl object-cover aspect-[16/8] shadow-xl" fetchpriority="high" decoding="async" /></div>}
        </div>
      </Container>
    </section>
  );
};

const Features = ({ c, lang }) => (
  <section className="py-16 sm:py-20">
    <Container>
      <SectionTitle title={c.title} subtitle={c.subtitle} lang={lang} />
      <div className="site-stagger grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {arr(c.items).map((it, i) => (
          <div key={i} className="site-card rounded-2xl border bg-white p-7 shadow-[0_10px_30px_rgba(16,24,40,0.06)] transition-shadow hover:shadow-[0_14px_36px_rgba(16,24,40,0.10)]" style={{ borderColor: 'var(--site-border)' }}>
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: 'rgba(var(--site-primary-rgb),0.08)' }}>
              <Icon name={it.icon} size={22} style={{ color: 'var(--site-primary)' }} />
            </div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--site-ink)' }}>{pick(it.title, lang)}</h3>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--site-muted)' }}>{pick(it.description, lang)}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const About = ({ c, lang }) => (
  <section className="py-16 sm:py-20" style={{ background: 'var(--site-soft)' }}>
    <Container>
      <div className="grid gap-12 lg:grid-cols-2 items-center">
        {c.image_url && <img src={c.image_url} alt="" className="w-full rounded-3xl object-cover aspect-[4/3] shadow-lg" loading="lazy" />}
        <div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: 'var(--site-ink)' }}>{pick(c.title, lang)}</h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--site-muted)' }}>{pick(c.body, lang)}</div>
        </div>
      </div>
    </Container>
  </section>
);

const Stats = ({ c, lang, motion }) => (
  <section className="py-12 sm:py-16">
    <Container>
      <div className="site-stagger grid grid-cols-2 gap-4 lg:grid-cols-4">
        {arr(c.items).map((it, i) => (
          <div key={i} className="site-card rounded-2xl border bg-white p-6 text-center" style={{ borderColor: 'var(--site-border)' }}>
            <div className="text-3xl sm:text-4xl font-semibold tabular-nums" style={{ color: 'var(--site-primary)' }}><CountUp value={txt(it.value, lang)} enabled={motion !== 'none'} /></div>
            <div className="mt-1 text-sm" style={{ color: 'var(--site-muted)' }}>{pick(it.label, lang)}</div>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const Gallery = ({ c, lang }) => (
  <section className="py-16 sm:py-20">
    <Container>
      <SectionTitle title={c.title} lang={lang} />
      <div className="site-stagger columns-2 md:columns-3 gap-4 [&>figure]:mb-4">
        {arr(c.images).map((im, i) => (
          <figure key={i} className="site-img-zoom break-inside-avoid overflow-hidden rounded-2xl">
            <img src={im.url} alt={pick(im.caption, lang)} className="w-full object-cover" loading="lazy" />
            {pick(im.caption, lang) && <figcaption className="mt-1.5 text-xs" style={{ color: 'var(--site-muted)' }}>{pick(im.caption, lang)}</figcaption>}
          </figure>
        ))}
      </div>
    </Container>
  </section>
);

const Pricing = ({ c, lang, onNavigate }) => (
  <section className="py-16 sm:py-20" style={{ background: 'var(--site-soft)' }}>
    <Container>
      <SectionTitle title={c.title} subtitle={c.subtitle} lang={lang} />
      <div className="site-stagger grid gap-6 md:grid-cols-3 items-stretch">
        {arr(c.plans).map((p, i) => (
          <div key={i} className={`site-card relative flex flex-col rounded-2xl border bg-white p-7 ${p.highlighted ? 'shadow-xl ring-2' : 'shadow-sm'}`} style={{ borderColor: 'var(--site-border)', '--tw-ring-color': 'var(--site-primary)' }}>
            {p.highlighted && <span className="absolute -top-3 left-6 rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: 'var(--site-primary)' }}>{lang === 'id' ? 'Populer' : 'Popular'}</span>}
            <h3 className="text-lg font-semibold" style={{ color: 'var(--site-ink)' }}>{pick(p.name, lang)}</h3>
            <div className="mt-3 flex items-baseline gap-1"><span className="text-3xl font-semibold" style={{ color: 'var(--site-ink)' }}>{txt(p.price, lang)}</span><span className="text-sm" style={{ color: 'var(--site-muted)' }}>{pick(p.period, lang)}</span></div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {arr(p.features).map((f, j) => <li key={j} className="flex gap-2 text-sm" style={{ color: 'var(--site-muted)' }}><Check size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--site-secondary)' }} />{pick(f, lang)}</li>)}
            </ul>
            <div className="mt-7"><Btn href="#contact" variant={p.highlighted ? 'primary' : 'secondary'} onNavigate={onNavigate}>{pick(p.cta_label, lang) || (lang === 'id' ? 'Pilih' : 'Choose')}</Btn></div>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const TestiCard = ({ it, lang, big }) => (
  <figure className={`site-card rounded-2xl border bg-white ${big ? 'mx-auto max-w-3xl p-10 text-center' : 'p-7'}`} style={{ borderColor: 'var(--site-border)' }}>
    <Quote size={big ? 30 : 22} className={big ? 'mx-auto' : ''} style={{ color: 'var(--site-secondary)' }} />
    <blockquote className={`mt-4 leading-relaxed ${big ? 'text-xl sm:text-2xl' : 'text-base'}`} style={{ color: 'var(--site-ink)' }}>“{pick(it.quote, lang)}”</blockquote>
    <figcaption className="mt-5 text-sm"><span className="font-semibold" style={{ color: 'var(--site-ink)' }}>{txt(it.name, lang)}</span>{pick(it.role, lang) && <span style={{ color: 'var(--site-muted)' }}> · {pick(it.role, lang)}</span>}</figcaption>
  </figure>
);

const Testimonials = ({ c, lang, motion }) => (
  <section className="py-16 sm:py-20">
    <Container>
      <SectionTitle title={c.title} lang={lang} />
      {c.layout === 'carousel'
        ? <Carousel items={arr(c.items)} animated={motion !== 'none'} render={(it) => <TestiCard it={it} lang={lang} big />} />
        : <div className="site-stagger grid gap-6 md:grid-cols-2 lg:grid-cols-3">{arr(c.items).map((it, i) => <TestiCard key={i} it={it} lang={lang} />)}</div>}
    </Container>
  </section>
);

const Cta = ({ c, lang, onNavigate }) => (
  <section className="py-16 sm:py-20">
    <Container>
      <div className="rounded-3xl px-8 py-14 text-center" style={{ background: 'var(--site-primary)' }}>
        <h2 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">{pick(c.title, lang)}</h2>
        {pick(c.subtitle, lang) && <p className="mt-3 text-white/85 text-lg max-w-2xl mx-auto">{pick(c.subtitle, lang)}</p>}
        {pick(c.button_label, lang) && <a href={c.button_link || '#contact'} onClick={(e) => onNavigate?.(e, c.button_link)} className="mt-8 inline-flex rounded-xl bg-white px-7 py-3 text-sm font-semibold shadow-md transition-colors hover:bg-white/90" style={{ color: 'var(--site-primary)' }}>{pick(c.button_label, lang)}</a>}
      </div>
    </Container>
  </section>
);

const Faq = ({ c, lang, motion }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-16 sm:py-20" style={{ background: 'var(--site-soft)' }}>
      <Container>
        <SectionTitle title={c.title} lang={lang} />
        <div className="mx-auto max-w-3xl divide-y rounded-2xl border bg-white" style={{ borderColor: 'var(--site-border)' }}>
          {arr(c.items).map((it, i) => (
            <div key={i}>
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between px-6 py-5 text-left font-medium" style={{ color: 'var(--site-ink)' }}>
                {pick(it.question, lang)}<Icons.ChevronDown size={18} className={`transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
              </button>
              <Collapse open={open === i} animated={motion !== 'none'}><div className="px-6 pb-5 text-sm leading-relaxed" style={{ color: 'var(--site-muted)' }}>{pick(it.answer, lang)}</div></Collapse>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

const Contact = ({ c, lang, siteSlug, pageId, isPreview }) => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [state, setState] = useState('idle');
  const submit = async (e) => {
    e.preventDefault();
    if (isPreview) return;
    setState('busy');
    try {
      await api.post(`/public/sites/${siteSlug}/forms`, { page_id: pageId, data: form });
      setState('done');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setState('error');
    }
  };
  const L = lang === 'id' ? { name: 'Nama', email: 'Email', msg: 'Pesan', send: 'Kirim Pesan', ok: 'Terima kasih! Pesan Anda telah terkirim.', err: 'Gagal mengirim. Coba lagi.' } : { name: 'Name', email: 'Email', msg: 'Message', send: 'Send Message', ok: 'Thank you! Your message was sent.', err: 'Failed to send. Try again.' };
  return (
    <section id="contact" className="py-16 sm:py-20">
      <Container>
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: 'var(--site-ink)' }}>{pick(c.title, lang)}</h2>
            {pick(c.subtitle, lang) && <p className="mt-3 text-lg" style={{ color: 'var(--site-muted)' }}>{pick(c.subtitle, lang)}</p>}
            <ul className="mt-8 space-y-4 text-sm" style={{ color: 'var(--site-ink)' }}>
              {txt(c.email) && <li className="flex items-center gap-3"><Mail size={18} style={{ color: 'var(--site-primary)' }} /><a href={`mailto:${txt(c.email)}`}>{txt(c.email)}</a></li>}
              {txt(c.phone) && <li className="flex items-center gap-3"><Phone size={18} style={{ color: 'var(--site-primary)' }} /><a href={`tel:${txt(c.phone)}`}>{txt(c.phone)}</a></li>}
              {pick(c.address, lang) && <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5" style={{ color: 'var(--site-primary)' }} /><span className="whitespace-pre-line">{pick(c.address, lang)}</span></li>}
            </ul>
          </div>
          {c.show_form !== false && (
            <form onSubmit={submit} className="rounded-2xl border bg-white p-7 shadow-sm space-y-4" style={{ borderColor: 'var(--site-border)' }} data-testid="public-contact-form">
              <input required placeholder={L.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2" style={{ borderColor: 'var(--site-border)' }} data-testid="public-contact-name" />
              <input required type="email" placeholder={L.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2" style={{ borderColor: 'var(--site-border)' }} data-testid="public-contact-email" />
              <textarea required rows={4} placeholder={L.msg} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2" style={{ borderColor: 'var(--site-border)' }} data-testid="public-contact-message" />
              <button type="submit" disabled={state === 'busy'} className="site-btn site-btn-primary inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold" data-testid="public-contact-submit">
                {state === 'busy' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{L.send}
              </button>
              {state === 'done' && <p className="text-sm text-emerald-700" data-testid="public-contact-success">{L.ok}</p>}
              {state === 'error' && <p className="text-sm text-rose-700">{L.err}</p>}
            </form>
          )}
        </div>
      </Container>
    </section>
  );
};

const TextBlock = ({ c, lang }) => (
  <section className="py-14 sm:py-16">
    <Container>
      <div className="mx-auto max-w-3xl">
        {pick(c.title, lang) && <h2 className="text-3xl font-semibold tracking-tight" style={{ color: 'var(--site-ink)' }}>{pick(c.title, lang)}</h2>}
        <div className="mt-4 text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--site-muted)' }}>{pick(c.body, lang)}</div>
      </div>
    </Container>
  </section>
);

const BLOCKS = { hero: Hero, features: Features, about: About, stats: Stats, gallery: Gallery, pricing: Pricing, testimonials: Testimonials, cta: Cta, faq: Faq, contact: Contact, text: TextBlock, ...STORE_BLOCKS, ...EXTRA_BLOCKS };

export const SectionBlock = (props) => {
  const Cmp = BLOCKS[props.section.type];
  if (!Cmp) return null;
  return <Cmp c={props.section.content || {}} {...props} />;
};

// ---------- Navbar / Footer ----------
const SOCIAL_ICON = { instagram: 'Instagram', facebook: 'Facebook', tiktok: 'Music2', youtube: 'Youtube', linkedin: 'Linkedin', twitter: 'Twitter', whatsapp: 'MessageCircle' };

/** Resolve a menu item to an href. */
const itemHref = (it, pages) => {
  if (it.type === 'external') return it.url || '#';
  if (it.type === 'anchor') { const a = it.anchor || ''; return a.startsWith('#') || a.startsWith('/') ? a : `#${a}`; }
  const p = pages.find((x) => x.id === it.page_id);
  return p ? (p.is_home ? '/' : `/${p.slug}`) : '/';
};
const menuItems = (site, pages, lang) => {
  const menu = site.navbar?.menu;
  if (Array.isArray(menu) && menu.length) return menu.map((it) => ({ ...it, href: itemHref(it, pages), text: pick(it.label, lang), children: (it.children || []).map((c) => ({ ...c, href: itemHref(c, pages), text: pick(c.label, lang) })) }));
  return pages.filter((p) => p.show_in_nav !== false).map((p) => ({ id: p.id, href: p.is_home ? '/' : `/${p.slug}`, text: pick(p.title, lang), children: [] }));
};

const NavLink = ({ it, onNavigate, active, mega }) => {
  const [open, setOpen] = useState(false);
  const ext = it.type === 'external';
  const link = (x, cls, style) => <a href={x.href} target={x.type === 'external' ? '_blank' : undefined} rel={x.type === 'external' ? 'noreferrer' : undefined} onClick={(e) => { if (x.type !== 'external') onNavigate?.(e, x.href); setOpen(false); }} className={cls} style={style} data-testid="site-nav-link">{x.text}</a>;
  if (!it.children?.length) return link(it, 'transition-colors hover:opacity-80', { color: active ? 'var(--site-primary)' : 'var(--site-ink)' });
  return (
    <div className={mega ? '' : 'relative'} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 transition-colors hover:opacity-80" style={{ color: 'var(--site-ink)' }} data-testid="site-nav-dropdown">{it.text}<Icons.ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} /></button>
      {open && (mega
        ? <div className="absolute left-0 right-0 top-full border-t bg-white shadow-xl" style={{ borderColor: 'var(--site-border)' }} data-testid="site-mega-panel"><Container className="grid gap-6 py-8 sm:grid-cols-3">{it.children.map((c) => <div key={c.id} className="rounded-xl p-3 transition-colors hover:bg-black/[0.03]">{link(c, 'block font-semibold', { color: 'var(--site-ink)' })}{c.desc && <p className="mt-1 text-xs" style={{ color: 'var(--site-muted)' }}>{pick(c.desc, 'id')}</p>}</div>)}</Container></div>
        : <div className="absolute left-0 top-full min-w-[200px] rounded-xl border bg-white p-2 shadow-xl" style={{ borderColor: 'var(--site-border)' }}>{it.children.map((c) => <div key={c.id}>{link(c, 'block rounded-lg px-3 py-2 text-sm hover:bg-black/[0.04]', { color: 'var(--site-ink)' })}</div>)}</div>)}
    </div>
  );
};

const Navbar = ({ site, pages, lang, setLang, currentSlug, onNavigate, langs, store }) => {
  const [open, setOpen] = useState(false);
  const nav = site.navbar || {};
  const layout = nav.layout || 'left';
  const items = menuItems(site, pages, lang);
  const isActive = (it) => it.href === (currentSlug === 'home' ? '/' : `/${currentSlug}`);
  const Brand = <a href="/" onClick={(e) => onNavigate?.(e, '/')} className="flex items-center gap-2 font-semibold text-lg" style={{ color: 'var(--site-ink)' }} data-testid="site-brand">
    {nav.logo_url ? <img src={nav.logo_url} alt="" className="h-8 w-auto" /> : <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white text-sm" style={{ background: 'var(--site-primary)' }}>{(nav.brand || site.name || 'S')[0]}</span>}
    <span>{nav.brand || site.name}</span></a>;
  const LangCta = <div className="flex items-center gap-3">
    {langs.length > 1 && <div className="inline-flex rounded-full border p-0.5 text-xs font-semibold" style={{ borderColor: 'var(--site-border)' }} data-testid="public-lang-toggle">{langs.map((l) => <button key={l} type="button" onClick={() => setLang(l)} className="rounded-full px-2.5 py-1 uppercase" style={lang === l ? { background: 'var(--site-primary)', color: '#fff' } : { color: 'var(--site-muted)' }} data-testid={`public-lang-${l}`}>{l}</button>)}</div>}
    {store && <StoreNavControls lang={lang} storeLinks={store.links} onNavigate={onNavigate} isPreview={store.isPreview} />}
    {pick(nav.cta_label, lang) && <a href={nav.cta_link || '#contact'} onClick={(e) => onNavigate?.(e, nav.cta_link)} className="site-btn site-btn-primary hidden sm:inline-flex rounded-xl px-4 py-2 text-sm font-semibold">{pick(nav.cta_label, lang)}</a>}
    <button type="button" className="md:hidden" onClick={() => setOpen(!open)} aria-label="menu" data-testid="site-nav-mobile-toggle">{open ? <X /> : <Menu />}</button></div>;
  const Links = <nav className={`hidden md:flex items-center gap-7 text-sm font-medium ${layout === 'mega' ? '' : ''}`} data-testid={`site-nav-${layout}`}>{items.map((it) => <NavLink key={it.id} it={it} onNavigate={onNavigate} active={isActive(it)} mega={layout === 'mega'} />)}</nav>;
  return (
    <header className={`sticky top-0 z-30 border-b bg-white/90 backdrop-blur ${layout === 'mega' ? 'relative' : ''}`} style={{ borderColor: 'var(--site-border)' }} data-testid="site-header">
      {layout === 'centered'
        ? <Container className="flex flex-col items-center gap-3 py-3"><div className="flex w-full items-center justify-between md:justify-center"><span className="md:hidden w-8" />{Brand}<div className="md:absolute md:right-8">{LangCta}</div></div>{Links}</Container>
        : <Container className="flex h-16 items-center justify-between gap-6">{Brand}{Links}{LangCta}</Container>}
      {open && (
        <div className="md:hidden border-t bg-white px-5 py-3 space-y-1" style={{ borderColor: 'var(--site-border)' }} data-testid="site-nav-mobile">
          {items.map((it) => <div key={it.id}><a href={it.href} target={it.type === 'external' ? '_blank' : undefined} rel="noreferrer" onClick={(e) => { setOpen(false); if (it.type !== 'external') onNavigate?.(e, it.href); }} className="block py-2 font-medium" style={{ color: 'var(--site-ink)' }}>{it.text}</a>
            {it.children?.map((c) => <a key={c.id} href={c.href} onClick={(e) => { setOpen(false); if (c.type !== 'external') onNavigate?.(e, c.href); }} className="block py-1.5 pl-4 text-sm" style={{ color: 'var(--site-muted)' }}>{c.text}</a>)}</div>)}
        </div>
      )}
    </header>
  );
};

const Footer = ({ site, lang, company, onNavigate, store }) => {
  const f = site.footer || {};
  const dark = (f.style || 'dark') === 'dark';
  const ink = dark ? '#fff' : 'var(--site-ink)';
  const muted = dark ? 'rgba(255,255,255,0.72)' : 'var(--site-muted)';
  const rich = f.company_name || f.description || (f.links || []).length || (f.socials || []).length || f.email || f.phone || f.address;
  const email = f.email || company?.email;
  const phone = f.phone || company?.phone;
  const copy = f.copyright || pick(f.text, lang) || `© ${new Date().getFullYear()} ${f.company_name || site.name}`;
  return (
    <footer className="border-t" style={{ borderColor: dark ? 'transparent' : 'var(--site-border)', background: dark ? 'var(--site-primary)' : 'var(--site-soft)' }} data-testid="site-footer">
      {rich && (
        <Container className="grid gap-10 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="text-lg font-semibold" style={{ color: ink }} data-testid="footer-company">{f.company_name || site.navbar?.brand || site.name}</div>
            {pick(f.description, lang) && <p className="mt-3 max-w-md text-sm leading-relaxed" style={{ color: muted }}>{pick(f.description, lang)}</p>}
            {(f.socials || []).filter((s) => s.url).length > 0 && <div className="mt-5 flex gap-2" data-testid="footer-socials">{f.socials.filter((s) => s.url).map((s, i) => <a key={i} href={s.url} target="_blank" rel="noreferrer" aria-label={s.platform} className="flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-80" style={{ background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(var(--site-primary-rgb),0.1)', color: dark ? '#fff' : 'var(--site-primary)' }}><Icon name={SOCIAL_ICON[s.platform] || 'Link'} size={16} /></a>)}</div>}
          </div>
          {(f.links || []).length > 0 && <div><div className="text-sm font-semibold" style={{ color: ink }}>{lang === 'id' ? 'Navigasi' : 'Navigation'}</div><ul className="mt-3 space-y-2 text-sm">{f.links.map((l, i) => <li key={l.id || i}><a href={l.href || '#'} onClick={(e) => !(l.href || '').startsWith('http') && onNavigate?.(e, l.href)} className="transition-opacity hover:opacity-80" style={{ color: muted }}>{pick(l.label, lang)}</a></li>)}</ul></div>}
          {(email || phone || f.address) && <div><div className="text-sm font-semibold" style={{ color: ink }}>{lang === 'id' ? 'Kontak' : 'Contact'}</div><ul className="mt-3 space-y-2 text-sm" style={{ color: muted }}>{email && <li className="flex items-center gap-2"><Mail size={14} /><a href={`mailto:${email}`}>{email}</a></li>}{phone && <li className="flex items-center gap-2"><Phone size={14} /><a href={`tel:${phone}`}>{phone}</a></li>}{f.address && <li className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /><span>{f.address}</span></li>}</ul></div>}
        </Container>
      )}
      {store && <div style={{ borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.15)' : 'var(--site-border)'}` }}><StoreTrustStrip lang={lang} dark={dark} /></div>}
      <div style={{ borderTop: rich || store ? `1px solid ${dark ? 'rgba(255,255,255,0.15)' : 'var(--site-border)'}` : 'none' }}>
        <Container className="flex flex-col sm:flex-row items-center justify-between gap-3 py-6 text-sm">
          <div style={{ color: muted }} data-testid="footer-copyright">{copy}</div>
          {!rich && company?.email && <div style={{ color: muted }}>{company.email}{company.phone ? ` · ${company.phone}` : ''}</div>}
          {f.show_pillot_badge !== false && <a href="https://pillot.id" target="_blank" rel="noreferrer" className="text-xs" style={{ color: muted }}>{lang === 'id' ? 'Dibuat dengan' : 'Built with'} <strong>Pillot</strong></a>}
        </Container>
      </div>
    </footer>
  );
};

// ---------- Analytics injection (GA4 + Meta Pixel) ----------
const useAnalytics = (site, enabled) => {
  useEffect(() => {
    if (!enabled || !site) return;
    const ga = site.ga_measurement_id;
    if (ga && !document.getElementById('pillot-ga')) {
      const s = document.createElement('script');
      s.id = 'pillot-ga';
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`;
      document.head.appendChild(s);
      const inline = document.createElement('script');
      inline.id = 'pillot-ga-inline';
      inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga.replace(/[^A-Za-z0-9-]/g, '')}');`;
      document.head.appendChild(inline);
    }
    const px = site.meta_pixel_id;
    if (px && !document.getElementById('pillot-pixel')) {
      const inline = document.createElement('script');
      inline.id = 'pillot-pixel';
      inline.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${px.replace(/\D/g, '')}');fbq('track','PageView');`;
      document.head.appendChild(inline);
    }
    if (site.custom_head && !document.getElementById('pillot-custom-head')) {
      const wrap = document.createElement('div');
      wrap.id = 'pillot-custom-head';
      wrap.innerHTML = site.custom_head;
      Array.from(wrap.querySelectorAll('script')).forEach((old) => {
        const s = document.createElement('script');
        Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
        s.text = old.text;
        document.head.appendChild(s);
      });
      Array.from(wrap.querySelectorAll('meta,link')).forEach((el) => document.head.appendChild(el));
      document.head.appendChild(wrap);
    }
  }, [site, enabled]);
};

/**
 * Full site renderer. `page` is the current page object. `interactive`=false for editor canvas.
 */
/** Pages that hold e-commerce blocks → hrefs used by product blocks (mini cart) and the account block. */
const storeLinksOf = (pages) => {
  const find = (type) => pages.find((p) => arr(p.sections).some((s) => s?.type === type && !s.hidden));
  const href = (p) => (p ? (p.is_home ? '/' : `/${p.slug}`) : undefined);
  return { checkout: href(find('store_checkout')), account: href(find('store_account')), shop: href(find('product_grid') || find('product_carousel') || find('product_featured') || find('product_category')) };
};
const hasStoreBlocks = (pages) => pages.some((p) => arr(p.sections).some((s) => STORE_SECTION_TYPES.includes(s?.type)));

export const SiteRenderer = ({ site, page, lang, setLang, onNavigate, isPreview = false, selectedId, onSelectSection, injectAnalytics = false, company, storeSlug, motionKey = 0, children }) => {
  const theme = useMemo(() => site.theme || {}, [site.theme]);
  useAnalytics(site, injectAnalytics && !isPreview);
  const vars = useMemo(() => {
    const dark = theme.mode === 'dark';
    const primary = readableColor(theme.primary, dark, '#1B2A6B');
    const secondary = readableColor(theme.secondary, dark, '#22C5AB');
    return {
      '--site-primary': primary,
      '--site-primary-rgb': hexToRgb(primary),
      '--site-secondary': secondary,
      '--site-secondary-rgb': hexToRgb(secondary),
      ...themeColors(theme),
      fontFamily: `'${theme.font || 'Inter'}', Inter, system-ui, sans-serif`,
    };
  }, [theme]);
  const attrs = useMemo(() => themeAttrs(theme), [theme]);
  const motion = themeValue(theme, 'motion');
  const pages = (site.pages || []).filter((p) => p.is_published !== false);
  const langs = site.languages || ['id', 'en'];
  const sections = arr(page?.sections).filter((s) => s && !s.hidden);
  const allPages = useMemo(() => [page, ...(site.pages || []).filter((p) => p.id !== page?.id)].filter(Boolean), [site.pages, page]);  // current page first so links prefer it
  const storeLinks = useMemo(() => storeLinksOf(allPages), [allPages]);
  const slug = storeSlug || site.company?.slug;
  const withStore = slug && hasStoreBlocks(allPages);
  const storeCtx = withStore ? { links: storeLinks, isPreview } : null;
  const body = (
    <div style={vars} {...attrs} className="site-root min-h-full text-[15px] antialiased" data-testid="site-renderer">
      <AnnouncementBar cfg={site.settings?.announcement} lang={lang} onNavigate={onNavigate} />
      <Navbar site={site} pages={pages} lang={lang} setLang={setLang} currentSlug={page?.slug} onNavigate={onNavigate} langs={langs} store={storeCtx} />
      {children}
      {!children && sections.length === 0 && (
        <div className="py-32 text-center" style={{ color: 'var(--site-muted)' }}>{lang === 'id' ? 'Halaman ini belum memiliki konten.' : 'This page has no content yet.'}</div>
      )}
      {!children && sections.map((s) => (
        <Reveal
          key={`${s.id}-${motionKey}`}
          effect={resolveMotion(theme, s)}
          id={`section-${s.id}`}
          onClick={onSelectSection ? (e) => { e.preventDefault(); onSelectSection(s.id); } : undefined}
          className={onSelectSection ? `relative scroll-mt-16 cursor-pointer transition-shadow ${selectedId === s.id ? 'ring-2 ring-inset ring-[hsl(var(--pillot-mint))]' : 'hover:ring-2 hover:ring-inset hover:ring-[hsl(var(--pillot-mint))]/40'}` : ''}
          data-testid={`canvas-section-${s.type}`}
        >
          {onSelectSection && selectedId === s.id && <span className="absolute left-3 top-3 z-10 rounded-md bg-[hsl(var(--pillot-mint))] px-2 py-0.5 text-[11px] font-semibold uppercase text-white">{s.type}</span>}
          <div id={s.anchor || undefined} className="site-section scroll-mt-20"><BlockBoundary type={s.type}><SectionBlock section={s} lang={lang} onNavigate={onNavigate} siteSlug={site.slug} pageId={page?.id} isPreview={isPreview} storeLinks={storeLinks} motion={motion} /></BlockBoundary></div>
        </Reveal>
      ))}
      <Footer site={site} lang={lang} company={company || site.company} onNavigate={onNavigate} store={storeCtx} />
    </div>
  );
  return withStore ? <StoreProvider slug={slug}>{body}</StoreProvider> : body;
};
